import React, { useState } from 'react';
import { Card, Typography, Empty, Tabs, Row, Col } from 'antd';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import styled from 'styled-components';

const { Title } = Typography;
const { TabPane } = Tabs;

const StyledCard = styled(Card)`
  .treemap-container {
    width: 100%;
    height: 350px;
  }
  .split-view {
    .treemap-half {
      width: 100%;
      height: 300px;
    }
  }
`;

interface TreemapData {
  name: string;
  children?: TreemapData[];
  value?: number;
  type?: 'plant' | 'animal';
  [key: string]: any;
}

interface SpeciesTreemapProps {
  data: TreemapData;
  loading?: boolean;
  viewMode?: 'split' | 'tabs';
}

const COLORS = {
  plant: ['#52c41a', '#73d13d', '#95de64', '#b7eb8f', '#d9f7be'],
  animal: ['#1890ff', '#40a9ff', '#69c0ff', '#91d5ff', '#bae7ff']
};

const CustomizedContent = (props: any) => {
  const { x, y, width, height, name, type, depth } = props;

  if (width < 40 || height < 30) return null;

  const colors = type === 'plant' ? COLORS.plant : COLORS.animal;
  const colorIndex = Math.min(depth, colors.length - 1);

  // Calculate font size based on available space
  const baseFontSize = Math.min(width / 8, height / 3, 14);
  const fontSize = Math.max(baseFontSize, 10);

  // Word wrap logic
  const words = name.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  // Estimate max characters per line based on width
  const maxCharsPerLine = Math.floor(width / (fontSize * 0.6));

  words.forEach((word: string) => {
    const testLine = currentLine + (currentLine ? ' ' : '') + word;
    if (testLine.length > maxCharsPerLine && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });
  if (currentLine) lines.push(currentLine);

  // Limit to 3 lines
  const displayLines = lines.slice(0, 3);
  const lineHeight = fontSize * 1.2;
  const totalHeight = displayLines.length * lineHeight;
  const startY = y + (height - totalHeight) / 2 + fontSize;

  // Use dark text for better visibility
  const textColor = type === 'animal' ? '#1a1a1a' : '#2d5016';

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: colors[colorIndex],
          stroke: '#fff',
          strokeWidth: 2,
          strokeOpacity: 1,
        }}
      />
      {width > 60 && height > 40 && displayLines.map((line, i) => (
        <text
          key={i}
          x={x + width / 2}
          y={startY + (i * lineHeight)}
          textAnchor="middle"
          fill={textColor}
          fontSize={fontSize}
          fontWeight="600"
          style={{
            textShadow: '0 1px 2px rgba(255,255,255,0.8)',
            paintOrder: 'stroke fill',
            stroke: 'rgba(255,255,255,0.6)',
            strokeWidth: '0.5px'
          }}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

const SpeciesTreemap: React.FC<SpeciesTreemapProps> = ({
  data,
  loading = false,
  viewMode = 'split'
}) => {
  const [activeTab, setActiveTab] = useState('plants');
  const hasData = data && data.children && data.children.length > 0;

  // Separate plants and animals
  const plantData = hasData && data.children ? data.children.filter((item: any) =>
    item.type === 'plant' || item.name.toLowerCase().includes('plant')
  ) : [];

  const animalData = hasData && data.children ? data.children.filter((item: any) =>
    item.type === 'animal' || item.name.toLowerCase().includes('animal')
  ) : [];

  const renderTreemap = (treeData: TreemapData[], type: 'plant' | 'animal') => {
    if (!treeData || treeData.length === 0) {
      return <Empty description={`No ${type} data available`} />;
    }

    return (
      <div className={viewMode === 'split' ? 'treemap-half' : 'treemap-container'}>
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={treeData}
            dataKey="value"
            aspectRatio={4 / 3}
            stroke="#fff"
            fill="#8884d8"
            content={<CustomizedContent />}
          >
            <Tooltip
              formatter={(value: any, name?: string, props?: any) => {
                return [
                  `${props?.payload?.children?.length || value} species`,
                  props?.payload?.name
                ];
              }}
            />
          </Treemap>
        </ResponsiveContainer>
      </div>
    );
  };

  if (!hasData) {
    return (
      <StyledCard
        bordered={false}
        loading={loading}
        title={<Title level={4} style={{ margin: 0 }}>Species Distribution</Title>}
      >
        <Empty description="No species data available" />
      </StyledCard>
    );
  }

  if (viewMode === 'split') {
    return (
      <StyledCard
        bordered={false}
        loading={loading}
        title={<Title level={4} style={{ margin: 0 }}>Species Distribution</Title>}
      >
        <div className="split-view">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                🌿 Plants
              </Typography.Text>
              {renderTreemap(plantData, 'plant')}
            </Col>
            <Col xs={24} md={12}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                🦎 Animals
              </Typography.Text>
              {renderTreemap(animalData, 'animal')}
            </Col>
          </Row>
        </div>
      </StyledCard>
    );
  }

  // Tabs view
  return (
    <StyledCard
      bordered={false}
      loading={loading}
      title={<Title level={4} style={{ margin: 0 }}>Species Distribution</Title>}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="🌿 Plants" key="plants">
          {renderTreemap(plantData, 'plant')}
        </TabPane>
        <TabPane tab="🦎 Animals" key="animals">
          {renderTreemap(animalData, 'animal')}
        </TabPane>
      </Tabs>
    </StyledCard>
  );
};

export default SpeciesTreemap;
